import type { LLMMessage, STPreset, WorldBook } from '@/IsekaiRPG/modules/llm/llmTypes';

export class MacroResolver {
  static normalizeMacroContext(source?: Record<string, string>): Record<string, string> {
    const context: Record<string, string> = {};
    if (!source) return context;

    for (const [key, value] of Object.entries(source)) {
      const normalizedKey = key.trim().toLowerCase();
      const normalizedValue = String(value ?? '').trim();
      if (!normalizedKey || !normalizedValue) continue;
      context[normalizedKey] = normalizedValue;
    }

    return context;
  }

  static applyMacrosToMessages(messages: LLMMessage[], macroContext: Record<string, string>): LLMMessage[] {
    return messages.map(message => ({
      ...message,
      content: this.resolveMacros(message.content, macroContext),
    }));
  }

  static applyMacrosToPreset(preset: STPreset, macroContext: Record<string, string>): STPreset {
    const prompts = preset.prompts.map(prompt => ({
      ...prompt,
      content: this.resolveMacros(prompt.content, macroContext),
      name: prompt.name ? this.resolveMacros(prompt.name, macroContext) : prompt.name,
    }));

    return {
      ...preset,
      prompts,
    };
  }

  static applyMacrosToWorldBook(
    worldBook: WorldBook | undefined,
    macroContext: Record<string, string>,
  ): WorldBook | undefined {
    if (!worldBook) return worldBook;

    return {
      ...worldBook,
      entries: worldBook.entries.map(entry => ({
        ...entry,
        name: this.resolveMacros(entry.name, macroContext),
        content: this.resolveMacros(entry.content, macroContext),
        keys: entry.keys.map(key => this.resolveMacros(key, macroContext)),
        secondary_keys: entry.secondary_keys.map(key => this.resolveMacros(key, macroContext)),
        outlet_name: this.resolveMacros(entry.outlet_name, macroContext),
      })),
    };
  }

  static resolveMacros(text: string, macroContext: Record<string, string>): string {
    if (!text || Object.keys(macroContext).length === 0) return text;

    return text.replace(/\{\{\s*([^{}]+?)\s*\}\}/g, (token, key: string) => {
      const normalizedKey = key.trim().toLowerCase();
      const value = macroContext[normalizedKey];
      return value !== undefined ? value : token;
    });
  }
}
